<template>
  <div class="b-carousel" ref="bCarousel">
    <div class="b-carousel-container">
      <div class="b-carousel-indicator" v-if="images.length && isShowIndicator">
        {{ currentIndex + 1 }}/{{ images.length }}
      </div>

      <div class="b-carousel-images">
        <div
          v-for="(image, index) in images"
          :class="{ active: index === currentIndex }"
          :key="index"
        >
          <img
            v-show="index === currentIndex"
            class="b-carousel-image"
            :src="image.imageUrl"
            :class="{ smallest: imageSmallest, small: imageSmall, full: imageFull }"
            alt=""
            @click="onPreviewImage()"
          />
        </div>
        <img
          v-if="!images.length"
          src="@/assets/images/slide-img-placeholder.png"
          class="b-carousel-image"
          :class="{ smallest: imageSmallest, small: imageSmall, full: imageFull }"
          alt=""
        />
      </div>

      <template v-if="isShowBtn">
        <div
          class="b-carousel-movebtn b-carousel-prev"
          :class="{ disabled: isDisabledPrev }"
          @click="handleBack"
        >
          <i class="icon icon-bor-arrow-left"></i>
        </div>
        <div
          class="b-carousel-movebtn b-carousel-next"
          :class="{ disabled: isDisabledNext }"
          @click="handleNext"
        >
          <i class="icon icon-bor-arrow-right"></i>
        </div>
      </template>
    </div>
    <p v-if="isRoomImage" class="truncate">
      {{ activeImage.imageExplanation }}
    </p>

    <div class="b-carousel-label" v-if="showLabel">
      {{ displayDetail }}
    </div>

    <div
      class="thumbnails"
      :style="transformThumbnail"
      v-if="images.length && isShowThumbnail"
    >
      <div
        v-for="(image, index) in images"
        :class="{ active: index === currentIndex }"
        :key="index"
        :style="widthImage"
      >
        <img
          :src="image.imageUrl"
          class="b-carousel-image"
          alt=""
          @click="onSelectImage(index)"
        />
      </div>
    </div>
    <Modal
      :visible="showPreviewImage"
      size="large"
      @cancel="showPreviewImage = false"
      @ok="showPreviewImage = false"
    >
      <div class="w-full">
        <img :src="activeImage && activeImage.imageUrl" class="image-preview" />
      </div>
    </Modal>
  </div>
</template>

<script>
import get from 'lodash/get'

import { Modal } from '@/components/primitive'
export default {
  name: 'BasicCarousel',
  components: {
    Modal
  },
  props: {
    images: {
      type: Array,
      default: () => []
    },
    activeIndex: {
      type: Number,
      default: 0
    },
    showLabel: {
      type: Boolean,
      default: false
    },
    imageSmall: {
      type: Boolean,
      default: false
    },
    imageSmallest: {
      type: Boolean,
      default: false
    },
    imageFull: {
      type: Boolean,
      default: false
    },
    isShowIndicator: {
      type: Boolean,
      default: true
    },
    isShowThumbnail: {
      type: Boolean,
      default: true
    },
    numberThumbnail: {
      type: Number,
      default: 5
    },
    isRoomImage: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      currentIndex: 0,
      scrollIndex: 0,
      widthContainer: 0,
      showPreviewImage: false
    }
  },
  created() {
    if (this.activeIndex) {
      this.currentIndex = this.activeIndex
    }
  },
  mounted() {
    this.widthContainer = ~~this.$refs.bCarousel?.offsetWidth
  },
  computed: {
    displayDetail() {
      return get(this.activeImage, 'label')
    },

    transformThumbnail() {
      if (!this.images || this.images.length === 0) return null
      if (this.numberThumbnail >= this.images.length) return null
      if (this.currentIndex === 0) return null

      const widthItem = +this.widthContainer / this.numberThumbnail
      let fixedScroll = widthItem * this.currentIndex

      if (this.currentIndex + this.numberThumbnail > this.images.length)
        fixedScroll = widthItem * (this.images.length - this.numberThumbnail)
      else {
        fixedScroll = fixedScroll - widthItem / 2
      }
      return `transform: translateX(-${fixedScroll}px)`
    },

    widthImage() {
      const width = (100 / this.numberThumbnail).toFixed(2)
      return `width: ${width}%; min-width: ${width}%;`
    },

    isDisabledNext() {
      return this.currentIndex === this.images.length - 1
    },

    isDisabledPrev() {
      return !this.currentIndex
    },

    isShowBtn() {
      return this.images && this.images?.length > 1
    },

    activeImage() {
      return this.images?.[this.currentIndex] || ''
    },

    calculateNumberSlide() {
      return +this.numberThumbnail - 1
    }
  },

  methods: {
    handleBack() {
      if (this.isDisabledPrev) return

      this.currentIndex -= 1
      this.triggerEvent()
    },

    handleNext() {
      if (this.isDisabledNext) return

      this.currentIndex += 1
      this.triggerEvent()
    },

    triggerEvent() {
      if (this.scrollIndex < this.currentIndex) {
        this.scrollIndex++
      } else if (this.scrollIndex > this.currentIndex + this.calculateNumberSlide) {
        this.scrollIndex = this.currentIndex + this.calculateNumberSlide
      }

      this.$emit('change', {
        image: this.activeImage,
        index: this.currentIndex,
        total: this.images.length
      })
    },

    onSelectImage(index) {
      this.currentIndex = index
      this.triggerEvent()
    },

    onPreviewImage() {
      if (this.isRoomImage) {
        this.showPreviewImage = true
      }
    }
  },

  watch: {
    activeIndex(value) {
      this.currentIndex = value
    }
  }
}
</script>

<style lang="scss" scoped>
.image-preview {
  width: 100%;
  background-size: cover;
  background-color: #0d76bf;
  box-shadow: 0px 0px 1px 1px #f1ecec;
}

.b-carousel {
  @apply w-full overflow-hidden;

  &-container {
    @apply mb-4 relative;
  }

  &-images {
    display: flex;
    justify-content: center;
    > div {
      visibility: hidden;
      opacity: 0;
      transition: opacity 0.3s, visibility 0.3s;

      &.active {
        visibility: visible;
        opacity: 1;
        transition: opacity 0.3s, visibility 0.3s;
      }
    }
  }

  &-image {
    @apply w-full;
    min-height: 325px;
    max-height: 351px;
    object-fit: contain;
    &.smallest {
      height: 180px;
    }
    &.small {
      height: 250px;
    }
    &.full {
      height: 100%;
    }
  }

  &-label {
    @apply mt-4 break-words;
    font-size: 14px;
    line-height: 24px;
    color: #63666a;
    line-height: 21px;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &-indicator {
    position: absolute;
    top: 10px;
    right: 10px;
    border-radius: 8px;
    z-index: 2;
    margin-bottom: -30px;
    text-align: center;
    background: #f0f0f0;
    width: 50px;
    border: 2px solid #fff;
  }

  &-movebtn {
    border: none;
    color: $white;
    cursor: pointer;
    transition-duration: 0.3s;
    width: 22px;
    height: 30px;
    background: rgba(31, 32, 33, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    top: 50%;
    transform: translateY(-50%);
    position: absolute;

    &.disabled {
      background-color: #bebebe;
      pointer-events: none;
    }
  }

  &.icon {
    font-size: 18px;
  }

  &-prev {
    left: 0;
    border-radius: 0 4px 4px 0;
  }

  &-next {
    right: 0;
    border-radius: 4px 0 0 4px;
  }

  .text-epip {
    line-height: 21px;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.thumbnails {
  display: flex;
  margin-top: 1rem;
  transition: all 300ms ease;

  > div {
    height: 80px;
    border: 1px solid transparent;
    overflow: hidden;
    transition: all 300ms ease;
  }

  img {
    @apply w-full h-full cursor-pointer;
    min-height: unset;
    padding: 0 4px;
  }

  > .active {
    border-color: #da291c;
  }
}
@screen sm {
  .b-carousel {
    &-image {
      min-height: 200px;
    }

    &-label {
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2; /* number of lines to show */
      line-clamp: 2;
      -webkit-box-orient: vertical;
    }
  }
}
</style>